import { Phone, AlertTriangle, Clock, Droplets, Zap, Flame, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { CTABand } from "@/components/Layout";
import { CallNowButton, EstimateButton } from "@/components/CTAButtons";
import { business } from "@/data/site";
import { track } from "@/lib/analytics";

const Emergency = () => {
  useSeo({
    title: `24/7 Emergency Plumbing, Electrical & HVAC — New York | ${business.name}`,
    description: "Emergency plumber, electrician & HVAC repair in New York, available 24/7. Burst pipes, power failures, no heat or AC — we respond fast. Call now.",
  });

  const emergencies = [
    { icon: Droplets, t: "Plumbing Emergencies", items: ["Burst or frozen pipes", "Major leaks & flooding", "Sewage backups", "No hot water"] },
    { icon: Zap, t: "Electrical Emergencies", items: ["Power outages", "Sparking outlets or panels", "Burning smells", "Exposed or damaged wiring"] },
    { icon: Flame, t: "HVAC Emergencies", items: ["No heat in winter", "No AC in a heatwave", "Furnace/boiler failure", "Gas or CO concerns*"] },
  ];

  return (
    <>
      {/* Urgent hero */}
      <section className="bg-[#059669] text-white" data-testid="emergency-hero">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white text-[#059669] px-4 py-2 mb-6 font-bold uppercase tracking-wide text-sm">
            <AlertTriangle className="w-5 h-5" /> Emergency? We're Here 24/7
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-5">Home Emergency in New York?<br />Call Us Right Now.</h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-8">Burst pipe, power failure, no heat or AC — our licensed team responds fast, day or night, across all five boroughs.</p>
          <a href={business.phoneHref} onClick={() => track("call_now_click", { location: "emergency" })} className="inline-flex items-center justify-center gap-3 bg-white text-[#059669] font-display font-black text-2xl sm:text-3xl px-8 py-5 hover:bg-[#0F2E4C] hover:text-white transition-colors" data-testid="emergency-call-btn">
            <Phone className="w-8 h-8" /> {business.phoneDisplay}
          </a>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center mt-8 text-sm text-white/90">
            {["Available 24/7/365", "Fast Response Times", "Licensed & Insured"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Clock className="w-4 h-4" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24" data-testid="emergency-types">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">We Handle Every Kind of Emergency</h2>
          <p className="text-[#475569] text-lg">If it can't wait, neither do we. Here's what we respond to around the clock.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {emergencies.map((e) => (
            <div key={e.t} className="border border-[#E2E8F0] p-8 bg-white">
              <div className="w-14 h-14 bg-[#0F2E4C] flex items-center justify-center mb-5"><e.icon className="w-7 h-7 text-white" /></div>
              <h3 className="font-display font-bold text-2xl mb-4">{e.t}</h3>
              <ul className="space-y-2">
                {e.items.map((i) => <li key={i} className="flex items-start gap-2 text-[#475569]"><CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0 mt-0.5" /> {i}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10 bg-[#0F2E4C] text-white p-6 flex items-start gap-3" data-testid="emergency-safety-note">
          <AlertTriangle className="w-6 h-6 text-[#34D399] shrink-0" />
          <p className="text-slate-300 text-sm">*If you smell gas or suspect carbon monoxide, leave the building immediately and call 911 and your utility provider first, then contact us. Your safety comes first.</p>
        </div>
      </section>

      <section className="bg-white border-t border-[#E2E8F0]" data-testid="emergency-cta">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">Not an Emergency?</h2>
          <p className="text-[#475569] text-lg mb-8">Schedule a standard visit or request a free estimate — we'll get back to you fast.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CallNowButton size="lg" testid="emergency-secondary-call" />
            <EstimateButton size="lg" testid="emergency-estimate-btn" />
          </div>
        </div>
      </section>

      <CTABand heading="Save Our Number Before You Need It" sub={`Program ${business.phoneDisplay} into your phone — emergencies never call ahead.`} />
    </>
  );
};

export default Emergency;
